import { useEffect, useState } from 'react';
import { useLaunchDarkly } from './providers/LaunchDarklyProvider';

/**
 * useFeatureFlag
 * @param flagKey
 * @param defaultValue
 */
export const useFeatureFlag = <T = boolean>(flagKey: string, defaultValue: T): T => {
  const ldClient = useLaunchDarkly();

  // Initialize with the actual flag value if client is ready, otherwise use default
  // This prevents a flash of incorrect state on initial render
  const [flagValue, setFlagValue] = useState<T>(() => {
    if (ldClient) {
      return ldClient.variation(flagKey, defaultValue);
    }
    return defaultValue;
  });

  useEffect(() => {
    if (!ldClient) return;

    // Evaluate the flag (in case it wasn't available during initial state)
    const currentValue = ldClient.variation(flagKey, defaultValue);
    setFlagValue(currentValue);

    // Subscribe to flag changes
    const handleChange = (newVal: T) => {
      setFlagValue(newVal);
    };

    ldClient.on(`change:${flagKey}`, handleChange);

    return () => {
      ldClient.off(`change:${flagKey}`, handleChange);
    };
  }, [ldClient, flagKey, defaultValue]);

  return flagValue;
};
